<template>
  <article ref="root" class="drc" :data-doctor-id="doctor.id" data-testid="doctor-card">
    <div class="drc__top">
      <img class="drc__avatar" :src="doctor.avatar || placeholder" alt="" />
      <div class="drc__who">
        <h3 class="drc__name">{{ doctor.name }}</h3>
        <p v-if="doctor.specialities" class="drc__spec">{{ doctor.specialities }}</p>
        <p v-if="doctor.qualifications" class="drc__qual">{{ doctor.qualifications }}</p>
      </div>
      <div class="drc__meta">
        <span class="drc__rating" :title="doctor.rating ? 'Average rating' : 'No ratings yet'">
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2l3.1 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.8 21l1.2-6.8-5-4.9 6.9-1z" /></svg>
          {{ doctor.rating ? Number(doctor.rating).toFixed(1) : 'New' }}
        </span>
        <span class="drc__dist" :title="distanceTitle">
          {{ distanceText }}
          <small v-if="doctor.distanceFromCentre" :title="centreTitle">from Bahrain centre</small>
        </span>
      </div>
    </div>

    <div class="drc__next" aria-live="polite">
      <template v-if="!next || next.status === 'idle' || next.status === 'loading'">
        <span class="drc__label">Next available</span>
        <div class="drc__chips" data-testid="card-slots-loading">
          <span v-for="n in 3" :key="n" class="drc__skel"></span>
        </div>
      </template>
      <template v-else-if="next.status === 'done' && next.slots.length">
        <span class="drc__label">Next available <strong>{{ dayText }}</strong></span>
        <div class="drc__chips">
          <SlotCard
            v-for="s in next.slots.slice(0, 3)"
            :key="s.id"
            selectable
            state="free"
            :start="s.startTime"
            :end="s.endTime"
            :type="s.type"
            :price="s.perSlotAmount"
            :data-slot-id="s.id"
            @click="$emit('open', { date: next.date, slot: s.id })"
          />
          <button
            v-if="next.slots.length > 3"
            type="button"
            class="drc__more"
            @click="$emit('open', { date: next.date })"
          >+{{ next.slots.length - 3 }} more</button>
        </div>
      </template>
      <p v-else class="drc__none" data-state="card-no-slots">
        {{ next.status === 'error' ? 'Times could not load. Open the profile to see them.' : 'No free times in the next few days' }}
      </p>
    </div>

    <div class="drc__foot">
      <span v-if="doctor.price" class="drc__price">From <strong>{{ doctor.price }}</strong></span>
      <span v-else></span>
      <button type="button" class="drc__view" @click="$emit('open', {})">View profile</button>
    </div>
  </article>
</template>

<script>
// TB-109: a clean search result card (next 3 times + more, View profile inside the card).
import { dayLabel } from '@/utils/slotPicker'
import SlotCard from '@/components/slot/SlotCard.vue'
import { DISTANCE_TITLE, CENTRE_TITLE, formatDistance } from '@/utils/distance'

export default {
  name: 'DoctorResultCard',
  components: { SlotCard },
  props: {
    // { id, name, specialities, qualifications, avatar, rating, distance, distanceFromCentre, price }
    doctor: { type: Object, required: true },
    // { status: idle|loading|done|error, date, slots[] }
    next: { type: Object, default: null }
  },
  emits: ['open', 'visible'],
  data () {
    return { placeholder: 'https://upload.wikimedia.org/wikipedia/commons/7/7c/Profile_avatar_placeholder_large.png', io: null }
  },
  computed: {
    distanceTitle () { return DISTANCE_TITLE },
    centreTitle () { return CENTRE_TITLE },
    distanceText () {
      const n = Number(this.doctor.distance)
      return Number.isFinite(n) && n > 0 ? formatDistance(n) : ''
    },
    dayText () { return this.next && this.next.date ? dayLabel(this.next.date) : '' }
  },
  mounted () {
    if (typeof window !== 'undefined' && 'IntersectionObserver' in window) {
      this.io = new window.IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.$emit('visible')
          this.io.disconnect()
          this.io = null
        }
      }, { rootMargin: '200px' })
      this.io.observe(this.$refs.root)
    } else {
      this.$emit('visible')
    }
  },
  beforeUnmount () {
    if (this.io) this.io.disconnect()
  },
  methods: {
  }
}
</script>

<style scoped>
.drc {
  display: flex; flex-direction: column; gap: 1rem; min-width: 0;
  padding: 1.25rem; border-radius: var(--tb-radius-card);
  background: var(--tb-bg-surface); border: 1px solid var(--tb-border); box-shadow: var(--tb-shadow-sm);
  transition: box-shadow 0.2s ease, transform 0.2s ease;
}
.drc:hover { box-shadow: var(--tb-shadow-md); transform: translateY(-2px); }
.drc__top { display: flex; gap: 1rem; align-items: flex-start; min-width: 0; }
.drc__avatar { width: 4rem; height: 4rem; border-radius: 50%; object-fit: cover; flex: none; border: 2px solid var(--tb-bg-surface); box-shadow: var(--tb-shadow-sm); }
.drc__who { flex: 1; min-width: 0; }
.drc__name { margin: 0; font-size: 1.05rem; font-weight: 800; color: var(--tb-text-primary); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.drc__spec { margin: 0.15rem 0 0; font-size: 0.875rem; color: var(--tb-text-body); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.drc__qual { margin: 0.15rem 0 0; font-size: 0.78rem; color: var(--tb-text-muted); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.drc__meta { flex: none; display: flex; flex-direction: column; align-items: flex-end; gap: 0.3rem; font-size: 0.8rem; color: var(--tb-text-muted); }
.drc__rating { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.15rem 0.55rem; border-radius: 999px; background: var(--tb-primary-container); color: var(--tb-primary-text); font-weight: 800; }
.drc__rating svg { width: 0.85rem; height: 0.85rem; fill: currentColor; }
.drc__dist { text-align: right; }
.drc__dist small { display: block; font-size: 0.7rem; }

.drc__next { border-top: 1px solid var(--tb-border); padding-top: 0.85rem; min-height: 4.2rem; }
.drc__label { display: block; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.04em; font-weight: 700; color: var(--tb-text-muted); margin-bottom: 0.5rem; }
.drc__label strong { color: var(--tb-text-primary); text-transform: none; letter-spacing: 0; font-size: 0.85rem; margin-left: 0.25rem; }
.drc__chips { display: flex; flex-wrap: wrap; gap: 0.5rem; }
.drc__chips .sc { flex: 1 1 8rem; max-width: 11rem; }
.drc__more { padding: 0.45rem 0.6rem; border: 0; background: none; color: var(--tb-primary-text); font-weight: 700; font-size: 0.85rem; cursor: pointer; }
.drc__none { margin: 0; color: var(--tb-text-muted); font-size: 0.875rem; }
.drc__skel {
  width: 8.5rem; height: 3.6rem; border-radius: var(--tb-radius-control);
  background: linear-gradient(90deg, var(--tb-bg-muted) 25%, var(--tb-bg-subtle) 50%, var(--tb-bg-muted) 75%);
  background-size: 200% 100%; animation: drc-shimmer 1.2s ease-in-out infinite;
}
@keyframes drc-shimmer { from { background-position: 200% 0; } to { background-position: -200% 0; } }

.drc__foot { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.drc__price { font-size: 0.875rem; color: var(--tb-text-muted); }
.drc__price strong { color: var(--tb-text-primary); }
.drc__view {
  padding: 0.6rem 1.4rem; border-radius: 999px; border: 0;
  background: var(--tb-primary); color: var(--tb-primary-contrast); font-weight: 800; font-size: 0.875rem; cursor: pointer;
}
.drc__view:hover { background: var(--tb-primary-hover); }
@media (max-width: 480px) {
  .drc__top { flex-wrap: wrap; }
  .drc__who { flex-basis: calc(100% - 5rem); }
  .drc__name { white-space: normal; }
  .drc__meta { width: 100%; flex-direction: row; align-items: center; justify-content: flex-start; gap: 0.75rem; }
  .drc__dist { text-align: left; }
  .drc__dist small { display: inline; margin-left: 0.25rem; }
}
@media (prefers-reduced-motion: reduce) {
  .drc { transition: none; }
  .drc:hover { transform: none; }
  .drc__skel { animation: none; }
}
</style>
